import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect } from "react";
import { RoutesEnum, StatusStateEnum, TrendReportTypeEnum } from "~/types/enums";
import { Alert, Badge, Box, Button, Card, Grid, Icon, Spinner, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { usePostHog } from "posthog-js/react";
import Markdown from "~/components/Markdown";
import TrendingTopicCard from "~/components/TrendingTopicCard";
import trendReportSelectors from "~/store/trendReports/selectors";
import trendReportsActionCreators from "~/store/trendReports/actions";
import styled from "styled-components";
import { useQueryState } from "~/hooks/common";
import StyledIcon from "~/components/StyledIcon";
import { helpers } from "~/lib/helpers";
import lists from "~/lib/lists";
import { useHistory } from "react-router-dom";
const StyledBox = styled(Box)`
  overflow: auto;
  position: relative;
  width: calc(100% + ${_ref => {
  let {
    theme
  } = _ref;
  return 26 / (theme.typography.fontSize / theme.spacing.step);
}}rem);

  &::-webkit-scrollbar {
    margin-left: 200px;
    width: 4px;
    height: 8px;
    background: transparent;
  }
  &::-webkit-scrollbar-track {
    background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.grey.main;
}};
    margin-left: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return 13 / (theme.typography.fontSize / theme.spacing.step);
}}rem;
    margin-right: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return 13 / (theme.typography.fontSize / theme.spacing.step);
}}rem;
  }
`;
const BoxWrapper = styled.div`
  &:after {
    content: "";
    position: absolute;
    z-index: 1;
    width: 20px;
    height: calc(100% - 10px);
    top: 0;
    right: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return -13 / (theme.typography.fontSize / theme.spacing.step);
}}rem;
    background: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.colors.gradients.secondaryBackground(90);
}};
  }
`;
const ProductTrendsCard = _ref7 => {
  let {
    ...rest
  } = _ref7;
  const {
    t
  } = useTranslation();
  const historyReports = useSelector(trendReportSelectors.getHistoryReports(TrendReportTypeEnum.PRODUCT));
  const [queryParams] = useQueryState({
    reportId: historyReports[0]?.id
  });
  const posthog = usePostHog();
  const {
    reportId
  } = queryParams;
  const report = useSelector(trendReportSelectors.getReport(reportId));
  const reportStatus = useSelector(trendReportSelectors.getStatusByReport(reportId));
  const reportError = useSelector(trendReportSelectors.getErrorByReport(reportId));
  const historyStatus = useSelector(trendReportSelectors.getHistoryStatus(TrendReportTypeEnum.PRODUCT));
  const historyError = useSelector(trendReportSelectors.getHistoryError(TrendReportTypeEnum.PRODUCT));
  const [selectedType, setSelectedType] = React.useState("all");
  const error = historyError || reportError;
  const dispatch = useDispatch();
  const trends = report?.trends.filter(trend => !trend.isDismissed && !trend.theme.topic?.isFollowed) || [];
  const trendsByType = trends.filter(trend => trend.workType === selectedType) || [];
  const byType = Object.fromEntries(Object.entries(helpers.groupByCount(trends.filter(trend => !trend.isDismissed) || [], "workType")).sort((a, b) => b[1] - a[1]).map(_ref8 => {
    let [key, value] = _ref8;
    return [key, value];
  }));
  const customerTrends = trends.filter(trend => trend.isCustomerRelated);
  const newTrends = trends.filter(trend => !trend.isRecurrent);
  const recurrentTrends = trends.filter(trend => trend.isRecurrent);
  const history = useHistory();
  const filteredTrends = () => {
    if (selectedType === "all") {
      return trends;
    }
    if (selectedType === "customer") {
      return customerTrends;
    }
    if (selectedType === "new") {
      return newTrends;
    }
    if (selectedType === "recurrent") {
      return recurrentTrends;
    }
    return trendsByType;
  };
  useEffect(() => {
    if (historyStatus === StatusStateEnum.IDLE) {
      dispatch(trendReportsActionCreators.fetchHistoryStarted(TrendReportTypeEnum.PRODUCT));
    }
  }, [dispatch, historyStatus]);
  useEffect(() => {
    if (reportId && reportStatus === StatusStateEnum.IDLE) {
      dispatch(trendReportsActionCreators.fetchOneStarted(reportId));
    }
  }, [dispatch, reportId, reportStatus]);
  const handleProductTrendsViewAll = () => {
    posthog.capture(`Product Trends – View all Trending Topics`);
    history.push(reportId ? `${RoutesEnum.PRODUCT_TRENDS}?reportId=${reportId}` : RoutesEnum.PRODUCT_TRENDS);
  };
  if (!report && (reportStatus === StatusStateEnum.LOADING || historyStatus === StatusStateEnum.LOADING)) {
    return /*#__PURE__*/React.createElement(Card, rest, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  if (report?.trends.length && !trends.length) {
    return /*#__PURE__*/React.createElement(Card, _extends({}, rest, {
      title: /*#__PURE__*/React.createElement(Grid, {
        cols: 2,
        gap: 3,
        template: "1fr auto"
      }, /*#__PURE__*/React.createElement(Text, {
        element: "h2",
        s: {
          mb: 1
        }
      }, t("ProductTrendsCard.productTrends")), /*#__PURE__*/React.createElement(Button, {
        onClick: handleProductTrendsViewAll,
        color: "grey",
        s: {
          px: 6,
          py: 2
        },
        fontSize: 3.5
      }, t("common.actions.viewAll")))
    }), /*#__PURE__*/React.createElement(Box, {
      direction: "column",
      align: "center",
      s: {
        my: 8
      }
    }, /*#__PURE__*/React.createElement(StyledIcon, {
      src: "tick",
      color: "translucentSuccess"
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      weight: "semiBold",
      display: "block",
      s: {
        mt: 6
      },
      align: "center",
      style: {
        maxWidth: "300px"
      }
    }, t("ProductTrendsCard.allFollowed"))));
  }
  return /*#__PURE__*/React.createElement(Card, _extends({}, rest, {
    title: /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      gap: 3,
      template: "1fr auto"
    }, /*#__PURE__*/React.createElement(Text, {
      element: "h2",
      s: {
        mb: 1
      }
    }, t("ProductTrendsCard.productTrends")), /*#__PURE__*/React.createElement(Button, {
      onClick: handleProductTrendsViewAll,
      color: "translucent",
      s: {
        pl: 6,
        pr: 4,
        py: 2
      },
      fontSize: 3.5,
      elemAfter: /*#__PURE__*/React.createElement(Icon, {
        src: "arrow",
        s: {
          mt: -0.5,
          ml: 1
        }
      })
    }, t("common.actions.viewAll")))
  }), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: error,
    linkColor: "text"
  })), trends.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    gap: 2,
    s: {
      mb: 5
    }
  }, /*#__PURE__*/React.createElement(Button, {
    fontSize: 3.4,
    s: {
      py: 1,
      pr: 1,
      pl: 3
    },
    color: selectedType === "all" ? "translucentPrimary" : "transparent",
    onClick: () => setSelectedType("all"),
    outline: false
  }, "All", /*#__PURE__*/React.createElement(Badge, {
    s: {
      py: 0.5,
      px: 2,
      ml: 1
    },
    color: "translucent"
  }, trends.length)), Object.keys(byType).map(key => /*#__PURE__*/React.createElement(Button, {
    key: key,
    fontSize: 3.4,
    s: {
      py: 1,
      pr: 1,
      pl: 3
    },
    color: selectedType === key ? "translucentPrimary" : "transparent",
    onClick: () => setSelectedType(key),
    outline: false
  }, lists.insights.workTypes.names[key], /*#__PURE__*/React.createElement(Badge, {
    s: {
      py: 0.5,
      px: 2,
      ml: 2
    },
    color: "translucent"
  }, byType[key]))), !!customerTrends.length && /*#__PURE__*/React.createElement(Button, {
    fontSize: 3.4,
    s: {
      py: 1,
      pr: 1,
      pl: 3
    },
    color: selectedType === "customer" ? "translucentPrimary" : "transparent",
    onClick: () => setSelectedType("customer"),
    outline: false
  }, "Customer", /*#__PURE__*/React.createElement(Badge, {
    s: {
      py: 0.5,
      px: 2,
      ml: 1
    },
    color: "translucent"
  }, customerTrends.length))), /*#__PURE__*/React.createElement(BoxWrapper, null, /*#__PURE__*/React.createElement(StyledBox, {
    wrap: "nowrap",
    align: "stretch",
    gap: 5,
    s: {
      p: 13,
      pb: 8,
      m: -13,
      mb: 0
    }
  }, filteredTrends().map(trend => /*#__PURE__*/React.createElement(TrendingTopicCard, {
    key: trend.id,
    width: "23.6%",
    reportId: reportId,
    trend: trend
  }))))) : /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    align: "center",
    s: {
      my: 8
    }
  }, /*#__PURE__*/React.createElement(StyledIcon, {
    src: "hourglassEmpty"
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "semiBold",
    display: "block",
    s: {
      mt: 6
    }
  }, t("ProductTrendsCard.noTrends")), /*#__PURE__*/React.createElement(Markdown, {
    color: "textSecondary",
    fontSize: 4,
    display: "block",
    s: {
      mt: 3,
      mb: 5
    },
    lineHeight: 6,
    style: {
      maxWidth: "400px"
    },
    align: "center",
    text: t("ProductTrendsCard.noTrendsInfo")
  })));
};
export default ProductTrendsCard;